import { ChangeEvent } from 'react';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { MarketEachWayPropsType } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/marketEachWay/MarketEachWay';

function numericValidatorError<K>(value: K): string | null {
    if (typeof value === 'string') {
        if (value.trim() === '') {
            return 'This field is required.';
        }
        return isNaN(parseInt(value, 10)) ? 'Only digits are allowed.' : null;
    }

    return typeof value === 'number' ? (isNaN(value) ? 'Only digits are allowed.' : null) : 'Only digits are allowed.';
}

function fractionalValidatorError<K>(value: K): string | null {
    if (typeof value === 'string') {
        if (value.trim() === '') {
            return 'This field is required.';
        }
        const splitValue = value.split('/');
        return splitValue.length !== 2
            ? 'Incorrect fractional number'
            : numericValidatorError(splitValue[0]) === null && numericValidatorError(splitValue[1]) === null
              ? null
              : 'Incorrect fractional number';
    }

    return typeof value === 'number' ? 'Incorrect fractional number' : null;
}

class FormConcept<T> {
    @observable inputValue: T | null = null;

    constructor(
        private readonly getModelValue: () => T | undefined,
        private readonly validator?: (value: T) => string | null
    ) {
        makeAutoObservable(this);
    }

    @action setValue = (value: T | null): void => {
        this.inputValue = value;
    };

    @computed get value(): T | null {
        if (this.inputValue !== null) {
            return this.inputValue;
        }
        const modelValue = this.getModelValue();
        return modelValue ?? null;
    }

    @computed get wasChange(): boolean {
        return this.inputValue !== null && this.inputValue !== this.getModelValue();
    }

    @computed get getError(): string | null {
        if (this.validator === undefined) {
            console.warn(
                'You try to valid input without a validator function. Please add a validator in the constructor'
            );
            return null;
        }
        return this.value !== null ? this.validator(this.value) : null;
    }

    @action reset = (): void => {
        this.inputValue = null;
    };
}

export class MarketEachWayState {
    eachWayOffered: FormConcept<boolean>;
    eachWayPlaces: FormConcept<number>;
    eachWayTermsWithBet: FormConcept<boolean>;
    eachWaytermsReduction: FormConcept<string>;

    constructor(
        readonly appState: AppState,
        readonly props: MarketEachWayPropsType
    ) {
        this.eachWayOffered = new FormConcept(() => this.props.marketId.getModel()?.eachWayOffered);
        this.eachWayPlaces = new FormConcept(
            () => this.props.marketId.getModel()?.eachWayTermsPlaces,
            numericValidatorError
        );
        this.eachWayTermsWithBet = new FormConcept(() => this.props.marketId.getModel()?.eachWay?.termsWithBet);
        this.eachWaytermsReduction = new FormConcept(
            () => this.props.marketId.getModel()?.eachWayTermsReduction,
            fractionalValidatorError
        );
        makeAutoObservable(this);
    }

    @action onChangeTermsReduction = (event: ChangeEvent<HTMLInputElement>): void => {
        this.eachWaytermsReduction.setValue(event.target.value);
    };

    @computed get places(): string {
        return this.eachWayPlaces.value !== null ? this.eachWayPlaces.value.toString() : '';
    }

    @action onChangeTermsPlaces = (event: ChangeEvent<HTMLInputElement>): void => {
        this.eachWayPlaces.setValue(Number(event.target.value));
    };

    @action onChangeEachWayOffered = (): void => {
        this.eachWayOffered.setValue(!this.eachWayOffered.value);
    };

    @action onChangeEachWayTermsWithBet = (): void => {
        this.eachWayTermsWithBet.setValue(!this.eachWayTermsWithBet.value);
    };

    @computed get isActiveSaveButton(): boolean {
        return (
            this.eachWayOffered.wasChange ||
            this.eachWayPlaces.wasChange ||
            this.eachWayTermsWithBet.wasChange ||
            this.eachWaytermsReduction.wasChange
        );
    }

    @action saveEachWay = async (): Promise<void> => {
        if (this.eachWayPlaces.getError !== null || this.eachWaytermsReduction.getError !== null) {
            return;
        }

        const places = this.eachWayPlaces.value !== null ? this.eachWayPlaces.value : 0;
        const reduction = this.eachWaytermsReduction.value !== null ? this.eachWaytermsReduction.value : '';
        const terms = this.eachWayOffered.value === false ? undefined : { places, reduction };
        const payload = {
            eachWay: {
                offered: this.eachWayOffered.value ?? false,
                termsWithBet: this.eachWayTermsWithBet.value ?? false,
                terms,
            },
        };

        await this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
        this.eachWayPlaces.reset();
        this.eachWaytermsReduction.reset();
    };
}
